import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

export type PublicCentralMemorial = {
  approval: { approvedAt: string | null; authority: "funeral_home"; status: "approved" };
  checkInEnabled: boolean;
  donationUrl: string;
  distribution: {
    canonicalUrl: string;
    description: string;
    externalAdapters: "disabled";
    imageAlt: string;
    imageUrl: string;
    indexing: "invite_only" | "public_indexable" | "public_noindex" | "unlisted" | "withdrawn";
    reporting: "disabled" | "enabled";
    robots: string;
    rolloutState: "pending_dec_07";
    title: string;
  };
  funeralHomeId: string;
  giftUrl: string;
  guestbook: Array<{ authorName: string; content: string; createdAt: string | null; photoUrl: string; reply: string }>;
  guestbookEnabled: boolean;
  indexing: "noindex";
  keepsakeEnabled: boolean;
  livestreamUrl: string;
  lovedOneName: string;
  maps: Array<{
    accessibilityLabel: string;
    confirmedAt: string;
    directionsUrl: string;
    displayAddress: string;
    latitude: number;
    longitude: number;
    manualDirections: string;
    precision: "country" | "region" | "locality" | "postal" | "street" | "rooftop";
    provider: string;
    providerVersion: string;
    sourceId: string;
    sourceType: "event" | "final_resting";
    status: "confirmed";
  }>;
  obituary: string;
  photoUrls: string[];
  provenance: Record<string, { approval: "approved"; sourceVersion: number; visibility: "public" }>;
  publicPageId: string;
  publication: {
    expiresAt: string | null;
    publishedAt: string | null;
    publisher: "funeral_home";
    themeVersion: string;
    withdrawalState: "active";
  };
  publishedAt: string | null;
  publishedVersion: number;
  qrEnabled: boolean;
  rsvpEnabled: boolean;
  schemaVersion: 1;
  serviceDetails: string;
  serviceEvents: Array<Record<string, string>>;
  slug: string;
  status: "published";
  theme: {
    approval: { approvedAt: string | null; status: "approved" | "fallback" };
    checks: { minimumContrast: number; passed: true };
    logo: { alt: string; url: string } | null;
    modes: {
      dark: PublicMemorialThemeMode;
      light: PublicMemorialThemeMode;
    };
    source: "approved" | "wehonor_fallback";
    themeId: string;
    typography: { body: "Manrope" | "system-ui"; heading: "Manrope" | "system-ui" };
    version: number;
  };
  updatedAt: string | null;
};
export type PublicMemorialThemeMode = {
  action: string;
  actionText: string;
  background: string;
  border: string;
  focus: string;
  muted: string;
  mutedText: string;
  surface: string;
  surfaceText: string;
  text: string;
};
export type PublicGuestbookPhoto = { base64: string; fileName: string; mimeType: "image/jpeg" | "image/png" };
const functions = getFunctions(firebaseApp, "us-east1");

export async function getPublishedCentralMemorial(funeralHomeId: string, slug: string) {
  const callable = httpsCallable<{ funeralHomeId: string; slug: string }, PublicCentralMemorial>(functions, "getPublishedCentralMemorial");
  return (await callable({ funeralHomeId, slug })).data;
}

/** Resolve the canonical whnr.me page without accepting tenant or case input. */
export async function getPublishedCentralMemorialBySlug(slug: string) {
  const callable = httpsCallable<{ slug: string }, PublicCentralMemorial>(
    functions,
    "getPublishedCentralMemorialBySlug",
  );
  return (await callable({ slug })).data;
}

export async function submitPublicCentralGuestbookEntry(input: { authorName: string; consentToContact: boolean; content: string; email?: string; funeralHomeId: string; phone?: string; photo?: PublicGuestbookPhoto | null; slug: string }) {
  const callable = httpsCallable<typeof input, { entryId: string; message: string; status: "pending" }>(functions, "submitPublicCentralGuestbookEntry");
  return (await callable(input)).data;
}

export async function submitPublicCentralMemorialReport(input: {
  category: "accessibility" | "accuracy" | "harmful_content" | "other" | "privacy";
  details: string;
  funeralHomeId: string;
  slug: string;
}) {
  const callable = httpsCallable<typeof input, { message: string; reportId: string; status: "pending" }>(
    functions,
    "submitPublicCentralMemorialReport",
  );
  return (await callable(input)).data;
}
